import { PlansSuggestionsRepositoryImpl } from '@commutifi-specific/Data/Repositories/CommuterPlans';
import { CommuteSectionCategory } from '@commutifi-specific/Domain/Models/CommuteSection';
import { Account, GetResponse, RequestParams } from '@commutifi-fe/interfaces';
import { PlanThirdPartyIntegration } from '@commutifi/models/Plan';
import type { ProviderBookingAllowance } from '@commutifi/models/Provider';
import { DaysValueEnum, InvitationSignupFormPayload } from 'shared/interfaces';
import { AccountWalletProps } from '@commutifi/models/AccountsWallets';
import { WalletEntryProps } from '@commutifi/models/WalletsEntries';
import { WorksiteProps } from '@commutifi/models/Worksites';
import { SurveyProps } from '@commutifi/models/Surveys';
import { ModeName } from '@commutifi/modes';
import { apiCall, AxiosHttpInstance, dashboardApiServer, JSON_HEADER } from '../../index';
import { FetchAccountVehicleResponse, accountVehicleDefaultFields, accountVehicleDefaultRelations } from '../vehicles';
import type { ListPlan, PlanBookingType } from '../commuterPlans';
import { PaymentMethod, PostPaymentMethodBody } from '../paymentMethods';
import { captureIsPlanValid } from '../../utils/global';
import { CardProps } from '../commutifiCards';
import type { BookingProps } from '../bookings';
import { WalletBalance } from '../wallets';
import endpoints from './endpoints';
export const apiDashboardServer = apiCall(dashboardApiServer);
import type { FetchMeData } from 'shared/interfaces';
export const fetchMe = (options?: {
  signal?: AbortSignal;
}): Promise<FetchMeData> => apiDashboardServer('authentication/me', {
  method: 'get',
  headers: JSON_HEADER,
  signal: options?.signal
});
export const fetchAccount = (id: string, queryParams: any): Promise<Account> => apiDashboardServer(endpoints.GET.Account.route(id), {
  method: 'get',
  params: queryParams
});
export const fetchAccountSubscriptions = (id: string) => apiDashboardServer(endpoints.GET.SubscriptionsLegacy.route(id), {
  method: 'get'
});
export const batchUpdateAccounts = (accountIds: string[], patch: Partial<Account>): Promise<Account[]> => apiDashboardServer(endpoints.PATCH.BatchAccounts.route(), {
  method: 'patch',
  headers: JSON_HEADER,
  data: JSON.stringify({
    ids: accountIds,
    data: patch
  })
});
export const updateOneAccount = (id: string | undefined, patch: Partial<Account>) => {
  if (!id) {
    throw new Error('Missing account id to update account');
  }
  return apiDashboardServer(endpoints.PATCH.Account.route(id), {
    method: 'patch',
    headers: JSON_HEADER,
    data: JSON.stringify(patch)
  });
};
export interface AccountCommuteData {
  arriveTime: string;
  carbon: number;
  commuteDays: DaysValueEnum[];
  commuteModes: ModeName[];
  cost: number;
  distance: number;
  duration: number;
  leaveTime: string;
  score: {
    global: number;
  };
}
export const fetchAccountCommuteData = (id: string): Promise<AccountCommuteData> => apiDashboardServer(endpoints.GET.CommuteData.route(id), {
  method: 'get'
});
export type PatchAccountPasswordBody = {
  oldPassword?: string;
  newPassword: string;
};

/**
 * Sets or changes an account's password.
 *
 * `oldPassword` is optional because this same route also sets the FIRST password
 * on an account that has none — the onboarding screen a passwordless user reaches
 * after signing in with OTP or a magic link. api-user decides which case applies
 * from the account's own state: omitting `oldPassword` against an account that
 * already has one is refused, so this is not a way to skip the check.
 */
export const patchAccountPassword = ({
  id,
  data
}: {
  id: string;
  data: PatchAccountPasswordBody;
}) => apiDashboardServer(endpoints.PATCH.AccountPassword.route(id), {
  method: 'patch',
  headers: JSON_HEADER,
  data: JSON.stringify(data)
});
export const addGroupToAccount = (groupId: string, accountId: string, queryParams: any = {}) => apiDashboardServer(endpoints.POST.AccountGroup.route(accountId, groupId), {
  method: 'post',
  params: queryParams,
  headers: JSON_HEADER
});
export const createAccount = (data: InvitationSignupFormPayload) => apiDashboardServer(endpoints.POST.Accounts.route(), {
  method: 'post',
  headers: JSON_HEADER,
  data
});
export type CreateGuestAccountBody = {
  name: string;
  enterpriseId: string;
};
export const createGuestAccount = (data: CreateGuestAccountBody): Promise<Account> => apiDashboardServer(endpoints.POST.GuestAccounts.route(), {
  method: 'post',
  headers: JSON_HEADER,
  data
});
export const deleteGuestAccount = (accountId: string): Promise<void> => apiDashboardServer(endpoints.DELETE.GuestAccount.route(accountId), {
  method: 'delete'
});
export const postEmailVerificationEmail = (data: {
  accountId?: string;
  email?: string;
}) => apiDashboardServer(endpoints.POST.SendEmailToVerifyAccount.route(), {
  method: 'post',
  data
});
export const postEmailVerification = (accountId: string, data: {
  emailVerificationToken: string;
}) => apiDashboardServer(endpoints.POST.VerifyEmail.route(accountId), {
  method: 'post',
  data
});
export interface PlanQueryParams extends RequestParams {
  id?: string | string[];
  enterpriseId?: string;
}
export const fetchPlans = async (accountId: string, params: PlanQueryParams): Promise<GetResponse<ListPlan>> => {
  const res = await apiDashboardServer<Awaited<ReturnType<typeof fetchPlans>>>(endpoints.GET.Plans.route(accountId), {
    method: 'get',
    params
  });
  res.records.forEach(plan => captureIsPlanValid(plan, {
    endpoint: endpoints.GET.Plans.route(accountId),
    params
  }));
  return res;
};
export interface PlanSuggestionsQueryParams extends RequestParams {
  enterpriseId?: string;
  commuteSectionCategories?: CommuteSectionCategory[];
}
export const fetchPlansSuggestions = (accountId: string, params: PlanSuggestionsQueryParams): Promise<GetResponse<ListPlan>> => apiDashboardServer(endpoints.GET.PlansSuggestions.route(accountId), {
  method: 'get',
  params
});
export interface AccountBookingQueryParams extends RequestParams {
  sort?: 'recurrentRelevance' | 'nonRecurrentRelevance';
  planId?: string;
  bookingType?: PlanBookingType;
  'ne:bookingType'?: PlanBookingType;
  'plan.thirdPartyIntegration'?: PlanThirdPartyIntegration;
  'ne:null:externalBookingId'?: 1;
}
export function fetchAccountBookings(accountId: string, queryParams: AccountBookingQueryParams): Promise<GetResponse<BookingProps>> {
  return apiDashboardServer(endpoints.GET.AccountBookings.route(accountId), {
    method: 'get',
    queryParams
  });
}
export const fetchAccountWalletEnrolments = (accountId: string, queryParams: RequestParams & {
  balance?: boolean;
} & Partial<Record<keyof AccountWalletProps, string | string[] | number>>): Promise<GetResponse<AccountWalletProps & WalletBalance>> => apiDashboardServer(endpoints.GET.AccountWalletEnrollments.route(accountId), {
  method: 'get',
  queryParams
});
export const fetchAccountWalletEntries = async (accountId: string, queryParams: RequestParams): Promise<GetResponse<WalletEntryProps>> => apiDashboardServer(endpoints.GET.AccountWalletEntries.route(accountId), {
  method: 'get',
  queryParams
});
export const fetchAccountCards = (accountId: string, queryParams: RequestParams): Promise<GetResponse<CardProps>> => apiDashboardServer(endpoints.GET.AccountCards.route(accountId), {
  method: 'get',
  queryParams
});
export interface PatchAccountsGroupsBody {
  accountIds: string[] | undefined;
  operation: 'assign' | 'unassign' | undefined;
  groupId: string;
}
export const patchAccountsGroups = (enterpriseId: string, body: PatchAccountsGroupsBody[]): Promise<{
  records: PatchAccountsGroupsBody[];
}> => apiDashboardServer(endpoints.PATCH.AccountsGroups.route(), {
  method: 'patch',
  headers: JSON_HEADER,
  queryParams: {
    enterpriseId
  },
  data: JSON.stringify(body)
});
export const makeAccountPlansSuggestionsRepository = (accountId: string) => PlansSuggestionsRepositoryImpl<PlanSuggestionsQueryParams>(endpoints.GET.PlansSuggestions.route(accountId), AxiosHttpInstance);
interface AccountKCIResponse {
  scoreVariation?: {
    value: number;
    since: string;
  };
  scores: {
    carbon: number | null;
    cost: number | null;
    time: number | null;
    global: number | null;
  };
  metrics: {
    time: {
      perDay: number | null;
    };
    carbon: {
      perDay: number | null;
    };
    cost: {
      perDay: number | null;
    };
  };
}
interface AccountRegionMetricsResponse {
  regionAverages: {
    time: number;
    carbon: number;
    cost: number;
  };
}
interface AccountScoresVariationResponse {
  points: {
    user: number;
    startDate: string;
    endDate: string;
  }[];
}
export const fetchAccountKCI = (id: string, queryParams: RequestParams): Promise<AccountKCIResponse> => apiDashboardServer(endpoints.GET.KCI.route(id), {
  method: 'GET',
  queryParams
});
export const fetchAccountRegionMetrics = (id: string, queryParams: RequestParams): Promise<AccountRegionMetricsResponse> => apiDashboardServer(endpoints.GET.RegionMetrics.route(id), {
  method: 'GET',
  queryParams
});
export const fetchAccountScores = (id: string, queryParams: RequestParams): Promise<AccountScoresVariationResponse> => apiDashboardServer(endpoints.GET.ScoreVariation.route(id), {
  method: 'GET',
  queryParams
});
export const fetchAccountPaymentMethods = (accountId: string, queryParams: RequestParams): Promise<GetResponse<PaymentMethod>> => apiDashboardServer(endpoints.GET.PaymentMethods.route(accountId), {
  method: 'get',
  queryParams
});
export interface PostCheckoutBody {
  startedAt: string;
  endedAt?: string;
  planId: string;
  accountId: string;
  quantity: number;
}
export const postAccountPaymentMethods = (accountId: string, data: PostPaymentMethodBody): Promise<PaymentMethod> => apiDashboardServer(endpoints.POST.AccountPaymentMethods.route(accountId), {
  method: 'post',
  data
});
export const fetchUserAccountVehicles = (accountId: string, queryParams: RequestParams): Promise<GetResponse<FetchAccountVehicleResponse>> => apiDashboardServer(endpoints.GET.UserAccountVehicles.route(accountId), {
  method: 'get',
  queryParams: {
    relations: accountVehicleDefaultRelations,
    fields: ['color', 'licensePlate', 'licensePlateState', 'vehicleId', 'accountId', ...accountVehicleDefaultFields.map(field => `vehicle.${field}`)],
    ...queryParams
  }
});
export const fetchAccountHasCommuteData = (accountId: string): Promise<{
  activeCommuteProfileIds: string[];
  count: number;
  hasActiveCommuteProfiles: boolean;
}> => apiDashboardServer(endpoints.GET.AccountHasCommuteData.route(accountId), {
  method: 'get'
});
export const fetchAccountSurveyAlreadyAnswered = (accountId: string, surveyId: string): Promise<{
  hasAnswers: boolean;
}> => apiDashboardServer(endpoints.GET.SurveyAlreadyAnswered.route(accountId, surveyId), {
  method: 'get'
});
export const fetchAccountDefaultSurvey = (accountId: string): Promise<{
  survey: SurveyProps;
  isMandatory: boolean;
}> => apiDashboardServer(endpoints.GET.AccountDefaultSurvey.route(accountId), {
  method: 'get'
});
import { AttributeAnswerType, AttributeProps } from '@commutifi/models/Attributes';
export { AttributeAnswerType };
export interface AccountAttribute extends Pick<Required<AttributeProps>, 'key' | 'referenceName' | 'answerType' | 'answerOptions'> {
  currentValue: string | string[] | null;
}
export interface UpdateAccountAttributesPayload {
  attributes: Record<string, string | string[] | null>;
}
export interface UpdateAccountAttributesResponse {
  id: string;
  attributes: Record<string, any>;
}

/**
 * GET /accounts/:accountId/attributes
 * Fetch all enterprise attributes for an account with current values
 */
export const fetchAccountAttributes = async (accountId: string): Promise<AccountAttribute[]> => {
  const response = await apiDashboardServer<AccountAttribute[]>(endpoints.GET.AccountAttributes.route(accountId), {
    method: 'get'
  });
  return response;
};

/**
 * PATCH /accounts/:accountId/attributes
 * Update user attribute values with validation (merges with existing attributes)
 */
export const updateAccountAttributes = (accountId: string, payload: UpdateAccountAttributesPayload): Promise<UpdateAccountAttributesResponse> => apiDashboardServer(endpoints.PATCH.AccountAttributes.route(accountId), {
  method: 'patch',
  headers: JSON_HEADER,
  data: JSON.stringify(payload)
});

/**
 * GET /accounts/:accountId/worksites
 * Fetch worksites available for a specific account (scoped by their enterprise)
 */
export const fetchAccountWorksites = (accountId: string, queryParams: RequestParams & {
  enterpriseId?: string;
} = {}): Promise<GetResponse<WorksiteProps>> => apiDashboardServer(endpoints.GET.AccountWorksites.route(accountId), {
  method: 'get',
  queryParams
});

/**
 * Where the account stands for each of the given plans, keyed by plan.
 *
 * A plan whose provider has no limit is absent from the answer — absent means unlimited — so the
 * common case comes back empty. The verdict is decided server-side: `isLimitReached` is read,
 * never recomputed here.
 */
export const fetchBookingLimits = (accountId: string, planIds: string[]): Promise<Record<string, ProviderBookingAllowance>> => apiDashboardServer(endpoints.GET.BookingLimits.route(accountId), {
  method: 'get',
  params: {
    planIds: planIds.join(',')
  }
});

// Re-exported rather than imported everywhere: this module is where the rest of the app reaches
// the booking-limit shape, and it is the server's own contract, not ours to restate.
export type { ProviderBookingAllowance };